import { orderBy } from 'lodash-es'

import {
  getNextPageParam,
  useDebouncedValue,
  useInfiniteQuery,
  useQuery,
} from '@fv/client-core'
import type { ClientAddressLookupResult } from '@fv/client-types'
import type { AddressLookupDTO } from '@fv/models'

import { shipperFetch } from '@/utils/shipperFetch'

import type { AddressBookListConfig, AddressBookResponse } from './types'
import { useAddressBook } from './useAddressBook'

type AddressBookSearchKeyArgs = Pick<
  AddressLookupDTO,
  'search' | 'context' | 'type'
>
const all = ['address-book'] as const
export const addressBookKeys = {
  all,
  byId: (id: string) => [...addressBookKeys.all, id],
  filterRoot: () => [...addressBookKeys.all, 'filter'],
  searchRoot: () => [...addressBookKeys.all, 'search'],
  filtered: ({ search, ...args }: AddressBookSearchKeyArgs) => [
    ...addressBookKeys.filterRoot(),
    args,
  ],
  search: (args: AddressBookSearchKeyArgs) => {
    const { search, ...rest } = args
    const filtered = addressBookKeys.filtered(rest)
    if (!search) return filtered
    return [...addressBookKeys.searchRoot(), rest, search]
  },
}

async function fetchAddresses(
  args: AddressLookupDTO,
): Promise<AddressBookResponse> {
  const { search, ...rest } = args
  return shipperFetch(`/addresses`, {
    query: {
      ...rest,
      ...(search && { search }),
    },
  })
}

interface AddressBookHookArgs extends AddressBookListConfig {
  enabled: boolean
  searchText: string
}

export function useAddressBookAddresses(args: AddressBookHookArgs) {
  const { enabled, postalCodeFilter, searchText, context, type } = args
  const debouncedSearchText = useDebouncedValue(searchText.trim())
  const search = postalCodeFilter || debouncedSearchText
  const searchArgs: Partial<AddressLookupDTO> = { search, context, type }
  const limit = 50
  return useInfiniteQuery<
    {
      data: ClientAddressLookupResult[]
      hasMore: boolean
    },
    Error,
    ClientAddressLookupResult[]
  >({
    queryKey: addressBookKeys.search(searchArgs),
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const results = await fetchAddresses({
        ...searchArgs,
        limit,
        skip: pageParam as number,
        includeAddressBook: true,
      })
      const hasMore = results.length !== undefined && results.length === limit
      return {
        data: results,
        hasMore,
      }
    },
    getNextPageParam,
    select: data => {
      return orderBy(
        data.pages.flatMap(p => p.data),
        ['score', 'company'],
        ['desc', 'asc'],
      )
    },
    enabled: !!enabled,
  })
}

export const useAddressesFromStore = (enabled?: boolean) => {
  const addressBook = useAddressBook()
  const addressBookQuery = useAddressBookAddresses({
    enabled: addressBook.isOpen || enabled,
    context: addressBook.config.context,
    postalCodeFilter: addressBook.config.postalCodeFilter,
    searchText: addressBook.searchText,
    type: addressBook.config.type,
  })
  return addressBookQuery
}

export function useAddressBookEntry(id?: string) {
  return useQuery(
    addressBookKeys.byId(id),
    () => shipperFetch<ClientAddressLookupResult>(`/addresses/${id}`),
    { enabled: !!id },
  )
}
